/**
 * useProductVariantPricing — ViewModel for /products/variant-pricing.
 *
 * Composes usePaginatedList for the per-product variant list (SOP 02
 * §"List Pagination") keyed by selectedProductId so picking a
 * different product resets back to page 1 automatically. The products
 * catalogue rides on every list response via `extras` — same pattern
 * as useProductPricing.
 *
 * Exposes a single mutation, setSellingPrice, which appends a pricing
 * snapshot for the chosen variant; the list reloads so derived
 * profit/margin/status refresh.
 */

import { useCallback, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  ProductPickerEntry,
  ProductVariantRowDto
} from '../models/product-variant.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/product-variant.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type {
  ProductPickerEntry,
  ProductVariantRowDto
} from '../models/product-variant.model'

export interface UseProductVariantPricingReturn {
  readonly products: ProductPickerEntry[]
  readonly selectedProductId: string | null
  readonly rows: ProductVariantRowDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly selectProduct: (id: string | null) => void
  readonly setPage: (n: number) => void
  readonly reload: () => void
  /**
   * Set a variant's selling price (appends a pricing snapshot). On
   * success the list reloads so the row's price + status refresh.
   */
  readonly setSellingPrice: (
    variantId: string,
    sellingPricePaisa: number
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly products: ProductPickerEntry[]
    readonly variants: {
      readonly items: ProductVariantRowDto[]
      readonly total: number
      readonly page: number
      readonly pageSize: number
    }
  }
}

export function useProductVariantPricing(): UseProductVariantPricingReturn {
  const { authedFetch } = useAuth()
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)

  const paged = usePaginatedList<ProductVariantRowDto, ProductPickerEntry[]>({
    deps: [selectedProductId],
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        params.set('pageSize', String(pageSize))
        if (selectedProductId !== null) {
          params.set('product_id', selectedProductId)
        }
        const res = await authedFetch(
          `${APIROUTES.PRODUCTVARIANTPRICING}?${params.toString()}`,
          { signal }
        )
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.variants.items,
          total: body.data.variants.total,
          extras: body.data.products
        }
      },
      [authedFetch, selectedProductId]
    )
  })

  const setSellingPrice = useCallback(
    async (variantId: string, sellingPricePaisa: number) => {
      const r = await mutateJson(
        authedFetch,
        fillPath(APIROUTES.PRODUCTVARIANTPRICINGSELLINGPRICE, { id: variantId }),
        'PATCH',
        { selling_price_paisa: sellingPricePaisa }
      )
      if (!r.ok) return r
      paged.reload() // re-fetch so the row's price + derived status refresh
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    products: paged.extras ?? [],
    selectedProductId,
    rows: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    selectProduct: setSelectedProductId,
    setPage: paged.setPage,
    reload: paged.reload,
    setSellingPrice
  }
}
